import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { api } from "./api.js";
import { attachStream } from "./useStream.js";
import { ModePicker, Visualizer, useVisualizerMode } from "./visualizer/Visualizer.js";
import "./landing.css";

/**
 * Front page.
 *
 * Editorial "signal grid" landing page: paper hero beside the brand portal, a
 * broadcast ticker, the frequency feed with its chat log, the archive cards and
 * the range footer. The play controls are real — they stream the station's HLS
 * playlist through a single shared media element.
 */

const PAGE_TITLE = "SONIC SYNAPSE — Chromatic Resonance";

const TICKER = [
  "Now playing: Void Pulse — 'Chromatic Drift'",
  "Live from Studio 7 — Berlin",
  "128 Kbps / 24 bit stereo",
  "Next up: Nocturne Frequency 23:00 UTC",
  "Request line open — drop a track",
  "Broadcast reach: 47 countries",
  "Signal strength ████ 100%",
];

const CITIES: [string, string][] = [
  ["Berlin", "52.5° N"],
  ["Tokyo", "35.6° N"],
  ["New York", "40.7° N"],
  ["São Paulo", "23.5° S"],
  ["Lagos", "6.5° N"],
  ["Sydney", "33.8° S"],
];

const ARCHIVE = [
  { when: "Fri 23:00 UTC", title: "Void Pulse", host: "DJ Nocturne", img: "/img/archive-1.webp" },
  { when: "Sat 21:00 UTC", title: "Analog Ghosts", host: "Kairo", img: "/img/archive-2.webp" },
  { when: "Sun 02:00 UTC", title: "Industrial Hum", host: "Sector Null", img: "/img/archive-3.webp" },
];

interface ChatMessage {
  who: string;
  text: string;
}

const SEED_CHAT: ChatMessage[] = [
  { who: "WAVE_RIDER", text: "this drop is unreal 🔥" },
  { who: "BASS_GHOST", text: "berlin studio sounds insane tonight" },
  { who: "NULL_SECTOR", text: "requesting the void pulse b2b" },
  { who: "KAIRO", text: "greetings from tokyo" },
  { who: "FREQ_MONK", text: "128bpm perfect running tempo" },
  { who: "ANON_497", text: "signal received — locked in" },
];

/** Bars are decorative, so a stable pseudo-random spread keeps them from jumping on re-render. */
const OSC_DURATIONS = [0.5, 0.7, 0.9, 1.1];

function useBars(count: number): { height: number; duration: number; delay: number }[] {
  return useMemo(
    () =>
      Array.from({ length: count }, (_, index) => ({
        height: 20 + ((index * 37) % 80),
        duration: OSC_DURATIONS[index % OSC_DURATIONS.length] ?? 0.7,
        delay: index * 0.05,
      })),
    [count],
  );
}

function RadioIcon(): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" />
      <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" />
      <circle cx="12" cy="12" r="2" />
      <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" />
      <path d="M19.1 4.9C23 8.8 23 15.1 19.1 19" />
    </svg>
  );
}

function PlayIcon(): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
      <polygon points="6 3 20 12 6 21 6 3" />
    </svg>
  );
}

function PauseIcon(): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="14" y="4" width="4" height="16" rx="1" />
      <rect x="6" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}

export function Landing(): JSX.Element {
  const audioRef = useRef<HTMLAudioElement>(null);
  const detachRef = useRef<(() => void) | null>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [chat, setChat] = useState<ChatMessage[]>(SEED_CHAT);
  const [draft, setDraft] = useState("");

  const feedBars = useBars(30);
  const meterBars = useBars(36);
  const [vizMode, setVizMode] = useVisualizerMode();
  const [vizReady, setVizReady] = useState(false);

  // The page title is part of the design; the console keeps its own.
  useEffect(() => {
    const previous = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previous;
    };
  }, []);

  // Idle chatter, the same way the reference feed keeps flowing.
  useEffect(() => {
    const timer = window.setInterval(() => {
      setChat((current) => {
        const next = [...current, { who: `ANON_${Math.floor(Math.random() * 999)}`, text: "signal received — locked in" }];
        return next.length > 60 ? next.slice(next.length - 60) : next;
      });
    }, 4000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [chat]);

  // One media element backs both play buttons; tear it down with the page.
  useEffect(
    () => () => {
      detachRef.current?.();
      detachRef.current = null;
    },
    [],
  );

  const togglePlayback = async (): Promise<void> => {
    const audio = audioRef.current;
    if (!audio || busy) return;
    setBusy(true);
    try {
      if (!detachRef.current) {
        // Resolve the live playlist on first use so a cold page costs no extra request.
        const status = await api.status().catch(() => null);
        // Audio elements take the audio-only playlist, never the master one:
        // the master advertises video renditions and hls.js picks one by
        // bandwidth, which leaves an <audio> element with an empty buffer and
        // a frozen playhead while it downloads fragments it can never render.
        const url = status?.hls.audioPlaylistUrl ?? "/hls/audio/a0.m3u8";
        detachRef.current = attachStream(audio, url);
      }
      if (audio.paused) {
        await audio.play();
        setPlaying(true);
      } else {
        audio.pause();
        setPlaying(false);
      }
    } catch {
      setPlaying(false);
    } finally {
      setBusy(false);
    }
  };

  const sendChat = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setChat((current) => [...current, { who: "YOU", text }]);
    setDraft("");
  };

  return (
    <div className="lp-root">
      <audio ref={audioRef} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} preload="none" />

      {/* ------------------------------------------------------------- hero */}
      <section className="lp-hero">
        <div className="lp-hero__left">
          <div className="lp-hero__top">
            <div className="lp-livebadge">
              <span className="lp-livebadge__dot" />
              <span>Live</span>
            </div>
            <p className="lp-eyebrow">01</p>
          </div>

          <div className="lp-hero__mid">
            <h1 className="lp-hero__title">
              <span>Essential </span>
              <span className="lp-gold">EDM</span>
              <br />
              <span className="lp-gold">Sessions</span>
              <span> Broadcast</span>
            </h1>
            <p className="lp-hero__sub">Hover The Portal · One-Click Playback</p>
          </div>

          <div>
            <div className="lp-hero__meta">
              <span>
                <RadioIcon />
                Audio 24/7
              </span>
              <span>·</span>
              <span>Video Studio Feed</span>
              <span>·</span>
              <span>Global Listeners: 12,408</span>
            </div>
            <div className="lp-stats">
              <div className="lp-stats__cell">
                <p className="lp-stats__label">BPM</p>
                <p className="lp-stats__value">128</p>
              </div>
              <div className="lp-stats__cell">
                <p className="lp-stats__label">KEY</p>
                <p className="lp-stats__value">A MIN</p>
              </div>
              <div className="lp-stats__cell">
                <p className="lp-stats__label">BITRATE</p>
                <p className="lp-stats__value">320K</p>
              </div>
              <div className="lp-stats__cell">
                <p className="lp-stats__label">LATENCY</p>
                <p className="lp-stats__value">42ms</p>
              </div>
            </div>
            <p className="lp-hero__now">Void Pulse — Live</p>
          </div>
        </div>

        <div className="lp-hero__right">
          <div className="lp-hero__media">
            <img src="/img/brand.webp" alt="Sonic Synapse brand artwork" />
            <div className="lp-hero__scrim" />
            <div className="lp-grain" style={{ opacity: 0.25, mixBlendMode: "overlay" }} />
          </div>

          <nav className="lp-nav">
            <a href="#archive">Archive</a>
            <a href="#feed">Live Feed</a>
            <a href="#schedule">Schedule</a>
            <a href="#privacy">Privacy</a>
            <a href="#terms">Terms</a>
          </nav>

          <div className="lp-portal">
            <button
              type="button"
              className="lp-portal__btn"
              aria-label="Play live broadcast"
              onClick={() => void togglePlayback()}
            >
              {playing ? <PauseIcon /> : <PlayIcon />}
            </button>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------------- ticker */}
      <div className="lp-ticker">
        <div className="lp-ticker__track">
          {[0, 1].map((copy) =>
            TICKER.map((item) => (
              <span className="lp-ticker__item" key={`${copy}-${item}`}>
                <span className="lp-ticker__diamond">◆</span>
                {item}
              </span>
            )),
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------- feed */}
      <div id="feed">
        <section className="lp-feed">
          <div className="lp-section-head">
            <div className="lp-section-head__meta">
              <p>View 02</p>
              <p>02</p>
            </div>
            <h2 className="lp-section-title">The Frequency Feed</h2>
          </div>

          <div className="lp-feed__grid">
            <div className="lp-feed__frame">
              <div className={`lp-feed__stage${vizReady ? " has-viz" : ""}`}>
                <Visualizer
                  getMedia={() => audioRef.current}
                  mode={vizMode}
                  onBackend={() => setVizReady(true)}
                />
                <div className="lp-grain" style={{ opacity: 0.2 }} />
                <div className="lp-feed__bars">
                  <div className="lp-feed__bars-row">
                    {feedBars.map((bar, index) => (
                      <span
                        key={index}
                        style={{
                          height: `${bar.height}%`,
                          animationDuration: `${bar.duration}s`,
                          animationDelay: `${bar.delay}s`,
                        }}
                      />
                    ))}
                  </div>
                  <p className="lp-feed__caption">Studio 7 · Berlin · Live Video Feed</p>
                </div>

                <div className="lp-rec">
                  <span className="lp-rec__dot" />
                  <span>REC</span>
                </div>

                <div className="lp-feed__tools">
                  <button type="button" aria-label="Picture in picture">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M21 9V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v10c0 1.1.9 2 2 2h4" />
                      <rect width="10" height="7" x="12" y="13" rx="2" />
                    </svg>
                  </button>
                  <button type="button" aria-label="Fullscreen">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="15 3 21 3 21 9" />
                      <polyline points="9 21 3 21 3 15" />
                      <line x1="21" x2="14" y1="3" y2="10" />
                      <line x1="3" x2="10" y1="21" y2="14" />
                    </svg>
                  </button>
                </div>

                <div className="viz-controls">
                  <ModePicker mode={vizMode} onChange={setVizMode} compact />
                </div>

                <div className="lp-feed__scrub">
                  <span />
                </div>
              </div>

              <div className="lp-chat">
                <div className="lp-chat__head">
                  <p>signal_stream.log</p>
                  <span className="lp-chat__count">{chat.length} MSG</span>
                </div>
                <div className="lp-chat__log" ref={logRef}>
                  {chat.map((message, index) => (
                    <div className="lp-chat__msg" key={`${message.who}-${index}`}>
                      <span className="lp-chat__who">{message.who}</span>
                      <span className="lp-chat__sep"> :: </span>
                      <span>{message.text}</span>
                    </div>
                  ))}
                </div>
                <form className="lp-chat__form" onSubmit={sendChat}>
                  <span className="lp-chat__prompt">$</span>
                  <input
                    type="text"
                    placeholder="transmit message…"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    aria-label="transmit message…"
                  />
                  <button type="submit" aria-label="Send message">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                      <path d="m21.854 2.147-10.94 10.939" />
                    </svg>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ---------------------------------------------------------- archive */}
      <div id="archive">
        <section className="lp-archive">
          <div className="lp-section-head">
            <div className="lp-section-head__meta">
              <p>View 03</p>
              <p>03</p>
            </div>
            <h2 className="lp-section-title">The Broadcast Archive</h2>
          </div>

          <div className="lp-archive__body">
            <p className="lp-archive__hint">
              Hover a sonic block — the shutter slices to reveal the tracklist beneath
            </p>
            <div className="lp-archive__grid">
              {ARCHIVE.map((entry) => (
                <article className="lp-card" key={entry.title}>
                  <div className="lp-card__media">
                    <img src={entry.img} alt={entry.title} loading="lazy" />
                    <div className="lp-grain" style={{ opacity: 0.25, mixBlendMode: "overlay" }} />
                  </div>
                  <div className="lp-card__body">
                    <p className="lp-card__when">{entry.when}</p>
                    <h3 className="lp-card__title">{entry.title}</h3>
                    <p className="lp-card__host">{entry.host}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* ----------------------------------------------------------- footer */}
      <footer className="lp-footer">
        <div className="lp-section-head">
          <div className="lp-section-head__meta">
            <p>View 04</p>
            <p>04</p>
          </div>
        </div>
        <p className="lp-footer__kicker">Broadcast Range · Global</p>
        <h2 className="lp-footer__big">47 Countries</h2>

        <div className="lp-cities">
          <div className="lp-cities__grid">
            {CITIES.map(([name, coord]) => (
              <div className="lp-city" key={name}>
                <div className="lp-wireframe" />
                <p className="lp-city__name">{name}</p>
                <p className="lp-city__coord">{coord}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lp-footer__base">
          <div className="lp-footer__row">
            <div>
              <p className="lp-footer__brand">Sonic Synapse</p>
              <p className="lp-footer__tagline">Architecting A Visual Frequency</p>
            </div>
            <div className="lp-footer__links">
              <a href="#archive">Archive</a>
              <a href="#feed">Live Feed</a>
              <a href="#schedule">Schedule</a>
              <a href="#privacy">Privacy</a>
              <a href="#terms">Terms</a>
            </div>
          </div>
          <p className="lp-footer__copy">
            © 2026 Sonic Synapse Broadcasting · All Frequencies Reserved · Built On The Signal Grid
          </p>
        </div>
      </footer>

      {/* --------------------------------------------------------- player bar */}
      <div className="lp-bar">
        <div className="lp-bar__inner">
          <div className="lp-bar__left">
            <button
              type="button"
              className="lp-bar__toggle"
              aria-label="Toggle playback"
              onClick={() => void togglePlayback()}
            >
              {playing ? <PauseIcon /> : <PlayIcon />}
            </button>
            <div className="lp-bar__meta">
              <p className="lp-bar__title">Chromatic Drift</p>
              <p className="lp-bar__sub">Void Pulse — Live</p>
            </div>
          </div>

          <div className="lp-bar__meters" aria-hidden="true">
            {meterBars.map((bar, index) => (
              <span
                key={index}
                style={{
                  height: `${bar.height}%`,
                  animationDuration: `${bar.duration}s`,
                  animationDelay: `${bar.delay}s`,
                }}
              />
            ))}
          </div>

          <div className="lp-bar__skips">
            <button type="button" aria-label="Previous">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polygon points="19 20 9 12 19 4 19 20" />
                <line x1="5" x2="5" y1="19" y2="5" />
              </svg>
            </button>
            <button type="button" aria-label="Next">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polygon points="5 4 15 12 5 20 5 4" />
                <line x1="19" x2="19" y1="5" y2="19" />
              </svg>
            </button>
          </div>

          <div className="lp-bar__volume">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
            <input
              type="range"
              min="0"
              max="100"
              defaultValue="70"
              aria-label="Volume"
              onChange={(event) => {
                if (audioRef.current) audioRef.current.volume = Number(event.target.value) / 100;
              }}
            />
          </div>

          <button type="button" className="lp-bar__clip">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
            </svg>
            <span>Clip 30s</span>
          </button>
        </div>
      </div>
    </div>
  );
}
